import React, { useState, useEffect, useCallback } from "react";
import Template from "../../components/Template";
import patrimonioService from "../../services/patrimonioService";
import ConfirmationModal from "../../components/ConfirmationModal";
import SuccessModal from "../../components/SuccessModal";
import AlertDialog from "../../components/AlertDialog";
import { useNavigate } from "react-router-dom";
import usePermission from "../../hooks/usePermission";

const GerenciarLocais = () => {
  const navigate = useNavigate();

  const [locais, setLocais] = useState([]);

  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const podeGerenciar = usePermission("GERENCIAR_LOCAIS_PATRIMONIO");

  const [formData, setFormData] = useState({
    nome: "",
    descricao: "",
    ativo: true,
  });

  // Modais
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [localToDelete, setLocalToDelete] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const fetchLocais = useCallback(async () => {
    try {
      const data = await patrimonioService.listarLocais();
      setLocais(data);
    } catch (error) {
      console.error("Erro ao carregar locais:", error);
      setAlertMessage("Erro ao carregar locais de patrimônio.");
    }
  }, []);

  useEffect(() => {
    fetchLocais();
  }, [fetchLocais]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const limparForm = () => {
    setFormData({ nome: "", descricao: "", ativo: true });
    setIsEditMode(false);
    setEditingId(null);
  };

  const handleSubmitForm = (e) => {
    e.preventDefault();
    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    setShowConfirmDialog(false);
    try {
      if (isEditMode && editingId) {
        await patrimonioService.atualizarLocal(editingId, {
          nome: formData.nome,
          descricao: formData.descricao,
          ativo: formData.ativo,
        });
      } else {
        await patrimonioService.criarLocal({
          nome: formData.nome,
          descricao: formData.descricao,
        });
      }
      setShowSuccessDialog(true);
      fetchLocais();
      limparForm();
    } catch (error) {
      console.error("Erro ao salvar local:", error);
      setAlertMessage(error.response?.data?.error || "Erro ao salvar local. Verifique e tente novamente.");
    }
  };

  const handleEdit = (local) => {
    setIsEditMode(true);
    setEditingId(local.id);
    setFormData({
      nome: local.nome,
      descricao: local.descricao || "",
      ativo: local.ativo,
    });
  };

  const handleDelete = (local) => {
    setLocalToDelete(local);
    setShowConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    setShowConfirmDelete(false);
    try {
      if (localToDelete) {
        await patrimonioService.excluirLocal(localToDelete.id);
        fetchLocais();
      }
      setLocalToDelete(null);
    } catch (error) {
      console.error("Erro ao excluir local:", error);
      setAlertMessage(error.response?.data?.error || "Erro ao excluir local.");
      setLocalToDelete(null);
    }
  };

  return (
    <Template>
      <div className="w-full px-4 py-6 flex flex-col gap-8">
        {/* Título */}
        <h1 className="text-2xl text-stone-800 font-medium">
          Locais de Patrimônio
        </h1>

        {/* Formulário de Criação/Edição */}
        {podeGerenciar && (
          <form onSubmit={handleSubmitForm} className="flex flex-col gap-4 bg-white p-4 rounded-md border border-slate-200">
            <div className="text-stone-600 text-base font-medium">
              {isEditMode ? "Editar Local" : "Cadastrar Novo Local"}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Nome */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm text-slate-700 font-medium">Nome</label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleInputChange}
                  className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                  placeholder="Ex: Sede, Filial, Depósito RJ..."
                  required
                />
              </div>

              {/* Situação (apenas na edição) */}
              {isEditMode && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm text-slate-700 font-medium">Situação</label>
                  <label className="inline-flex items-center gap-2 text-sm mt-2">
                    <input
                      type="checkbox"
                      name="ativo"
                      checked={formData.ativo}
                      onChange={handleInputChange}
                    />
                    <span>{formData.ativo ? "Ativo" : "Inativo"}</span>
                  </label>
                </div>
              )}
            </div>

            {/* Descrição */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm text-slate-700 font-medium">Descrição</label>
              <textarea
                name="descricao"
                rows={2}
                value={formData.descricao}
                onChange={handleInputChange}
                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
              />
            </div>

            {/* Botões */}
            <div className="flex items-center gap-4 mt-4">
              {isEditMode && (
                <button
                  type="button"
                  onClick={limparForm}
                  className="px-4 py-2 bg-stone-400 text-white text-sm font-medium rounded-md"
                >
                  Cancelar
                </button>
              )}

              <button
                type="submit"
                className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md"
              >
                {isEditMode ? "Salvar Alterações" : "Adicionar Local"}
              </button>
            </div>
          </form>
        )}

        {/* Listagem de Locais */}
        <div className="bg-white p-4 rounded-md border border-slate-200">
          <div className="text-stone-600 text-base font-medium mb-4">
            Lista de Locais
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-left border-collapse">
              <thead>
                <tr className="bg-stone-100">
                  <th className="px-4 py-2 border border-slate-200">Nome</th>
                  <th className="px-4 py-2 border border-slate-200">Descrição</th>
                  <th className="px-4 py-2 border border-slate-200">Situação</th>
                  <th className="px-4 py-2 border border-slate-200">Bens no local</th>
                  <th className="px-4 py-2 border border-slate-200">Ações</th>
                </tr>
              </thead>
              <tbody>
                {locais.length > 0 ? (
                  locais.map((local) => (
                    <tr key={local.id} className="border-b">
                      <td className="px-4 py-2 border border-slate-200">{local.nome}</td>
                      <td className="px-4 py-2 border border-slate-200">{local.descricao || "-"}</td>
                      <td className="px-4 py-2 border border-slate-200">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            local.ativo ? "bg-green-100 text-green-800" : "bg-stone-200 text-stone-600"
                          }`}
                        >
                          {local.ativo ? "Ativo" : "Inativo"}
                        </span>
                      </td>
                      <td className="px-4 py-2 border border-slate-200">
                        {local._count?.patrimonios ?? 0}
                      </td>
                      <td className="px-4 py-2 border border-slate-200">
                        {podeGerenciar && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleEdit(local)}
                              className="px-2 py-1 bg-stone-600 text-white rounded-md text-sm mr-2"
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(local)}
                              className="px-2 py-1 bg-red-600 text-white rounded-md text-sm"
                            >
                              Excluir
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-4 py-2 text-center text-slate-700">
                      Nenhum local cadastrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Botões de Ação */}
        <div className="flex items-center w-full gap-4">
          <div className="flex gap-4 ml-auto">
            <button
              type="button"
              className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
              onClick={() => navigate("/patrimonios")}
            >
              <img className="w-4 h-4" src="/images/icon/arrow-left.png" alt="Voltar" />
              <span>Voltar</span>
            </button>
          </div>
        </div>
      </div>

      {/* Confirmar Salvar */}
      <ConfirmationModal
        isVisible={showConfirmDialog}
        onConfirm={handleConfirmSubmit}
        onCancel={() => setShowConfirmDialog(false)}
        message="Deseja salvar este local?"
      />

      {/* Confirmar Excluir */}
      <ConfirmationModal
        isVisible={showConfirmDelete}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setShowConfirmDelete(false);
          setLocalToDelete(null);
        }}
        message="Deseja apagar este local?"
        textButtonConfirm="Sim"
      />

      {/* Sucesso */}
      <SuccessModal
        isVisible={showSuccessDialog}
        message="Local salvo com sucesso!"
        onClose={() => setShowSuccessDialog(false)}
      />

      {/* Alerta de erro */}
      <AlertDialog
        isVisible={!!alertMessage}
        message={alertMessage}
        onClose={() => setAlertMessage("")}
      />
    </Template>
  );
};

export default GerenciarLocais;
